import { EndpointSetting } from "@/constants/app/type";
import { EsimCountriesExtra, EsimCountry, EsimState } from "../types";

type EsimCountryLocaleBlock = {
  countryName?: string;
  continent?: string;
  capital?: string;
};

type EsimCountryApiRow = {
  code?: string;
  countryName?: string;
  continent?: string;
  capital?: string;
  vi?: EsimCountryLocaleBlock;
  en?: EsimCountryLocaleBlock;
};

const mapEsimCountryRow = (
  row: EsimCountryApiRow,
  languageCode: string,
): EsimCountry | null => {
  const code = `${row.code ?? ""}`.trim();
  if (!code) return null;

  const lang = languageCode.toLowerCase().startsWith("en") ? "en" : "vi";
  const locale =
    row[lang] ??
    row.vi ??
    row.en ?? {
      countryName: row.countryName,
      continent: row.continent,
      capital: row.capital,
    };

  return {
    code,
    countryName: `${locale.countryName ?? ""}`.trim() || code.toUpperCase(),
    continent: `${locale.continent ?? ""}`.trim(),
    capital: `${locale.capital ?? ""}`.trim(),
  };
};

const mapEsimCountryList = (
  raw: unknown,
  languageCode: string,
): EsimCountry[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => mapEsimCountryRow(row as EsimCountryApiRow, languageCode))
    .filter((country): country is EsimCountry => country !== null);
};

export const callbackEsimCountries: EndpointSetting["caller"] = (
  state: EsimState,
  action,
) => {
  const payload = (action.payload ?? {}) as Record<string, unknown>;
  const nested = payload.data as Record<string, unknown> | undefined;
  const requestBody = (action as { meta?: { arg?: { body?: Record<string, unknown> } } })
    .meta?.arg?.body;
  const languageCode =
    (requestBody?.language_code as string | undefined) ??
    (requestBody?.languageCode as string | undefined) ??
    "vi";

  const countries = mapEsimCountryList(
    payload.result ?? nested?.result ?? payload.countries ?? nested?.countries,
    languageCode,
  );

  const rawExtra = (payload.extra ?? nested?.extra) as Record<string, unknown> | null | undefined;
  const extra: EsimCountriesExtra | null =
    rawExtra && typeof rawExtra === "object"
      ? {
          continent_country: Array.isArray(rawExtra.continent_country)
            ? (rawExtra.continent_country as EsimCountriesExtra["continent_country"])
            : [],
          popular_countries: mapEsimCountryList(rawExtra.popular_countries, languageCode),
          regions_type: Array.isArray(rawExtra.regions_type)
            ? (rawExtra.regions_type as EsimCountriesExtra["regions_type"])
            : [],
        }
      : null;

  state.esim_countries.data.countries = countries;
  state.esim_countries.data.extra = extra;
  state.esim_countries.data.searchResults = countries;
};
